import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Select, MenuItem, FormControl,
  InputLabel, Alert, CircularProgress, Tabs, Tab, IconButton, Tooltip,
  LinearProgress, Checkbox, TableSortLabel, TablePagination, TextField,
  InputAdornment, Stack,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Chip } from '@mui/material';
import axios from 'axios';
import authService from '../../services/authService';

const API_URL = process.env.REACT_APP_API_URL || '';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// How often to re-check a file that is still pending/processing, and how many
// times before giving up. Reports take roughly 20s each and a CSV can hold
// several, so 5s x 120 covers ~10 minutes of work. The cap matters because a row
// can legitimately sit 'pending' forever — a lab that is not on the autoprocess
// allowlist waits for a human, and rows have been known not to flip — and without
// it the tab would poll for as long as it stayed open.
const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 120;

interface Lab {
  id: string;
  lab_id: string;
  name: string;
  is_active: boolean;
}

interface LabFile {
  filename: string;
  size_bytes: number;
  uploaded_at?: string;
  created_at?: string;
  // Processing status (input files only)
  status?: 'pending' | 'processing' | 'processed' | 'failed' | 'held' | 'resolved' | 'superseded';
  report_id?: string | null;
  output_filename?: string | null;
  dashboard_url?: string | null;
  processed_at?: string | null;
  last_error?: string | null;
  can_process?: boolean;
  // A bulk upload is one tracking row PER ORDER, so a file is routinely mixed
  // (e.g. 10 processed + 1 held). These describe the whole file.
  order_counts?: Record<string, number>;
  order_total?: number;
  held_orders?: HeldOrder[];
  // Output files only: the upload this report was generated from. Null for a
  // PDF the server could not trace back to a tracking row (pre-dates the
  // output_filename column and does not stem-match, or was dropped in by hand).
  source_filename?: string | null;
  panel_type?: string | null;
}

// One distinct upload represented in the output listing, for the source picker.
interface OutputSource {
  filename: string;
  report_count: number;
}

// Sentinel for the "could not be traced to an upload" bucket. Not a filename,
// so it can never collide with a real one.
const UNKNOWN_SOURCE = ' unknown';

interface HeldOrder {
  row_id: string;
  accession: string | null;
  reason: string | null;
  held_at?: string | null;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
}

type FolderType = 'input' | 'output';

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso?: string): string => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const FileUploadManager: React.FC = () => {
  const [labs, setLabs] = useState<Lab[]>([]);
  const [selectedLabId, setSelectedLabId] = useState<string>('');
  const [tab, setTab] = useState<FolderType>('input');
  const [inputFiles, setInputFiles] = useState<LabFile[]>([]);
  const [outputFiles, setOutputFiles] = useState<LabFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Output-tab list controls (sort/filter/select/paginate)
  type OutSortKey = 'filename' | 'size_bytes' | 'created_at';
  const [outSortKey, setOutSortKey] = useState<OutSortKey>('created_at');
  const [outSortDir, setOutSortDir] = useState<'asc' | 'desc'>('desc');
  const [outSearch, setOutSearch] = useState('');
  const [outDateFrom, setOutDateFrom] = useState('');  // yyyy-mm-dd
  const [outDateTo, setOutDateTo] = useState('');
  // '' = every upload. Otherwise an input CSV name, or UNKNOWN_SOURCE.
  const [outSource, setOutSource] = useState('');
  const [outSources, setOutSources] = useState<OutputSource[]>([]);
  const [outUnmatched, setOutUnmatched] = useState(0);
  const [outSelected, setOutSelected] = useState<Set<string>>(new Set());
  const [outPage, setOutPage] = useState(0);
  const [outRowsPerPage, setOutRowsPerPage] = useState(50);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const getHeaders = () => authService.getAuthHeaders();

  const fetchLabs = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/v1/lis-portal/labs`, { headers: getHeaders() });
      if (resp.data.success) {
        const activeLabs = (resp.data.labs || []).filter((l: Lab) => l.is_active);
        setLabs(activeLabs);
        if (activeLabs.length && !selectedLabId) {
          setSelectedLabId(activeLabs[0].lab_id);
        }
      } else {
        setError(resp.data.error || 'Failed to load labs');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load labs');
    }
  };

  // `quiet` is used by the background poll: it refreshes the rows without flashing
  // the spinner or clearing a message the user is still reading, and it swallows
  // transient network errors rather than replacing the list with an error banner.
  const fetchFiles = useCallback(async (labId: string, quiet = false) => {
    if (!labId) return;
    if (!quiet) {
      setLoading(true);
      setError(null);
    }
    try {
      const [inResp, outResp] = await Promise.all([
        axios.get(`${API_URL}/api/v1/lis-portal/labs/${labId}/files/input`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/v1/lis-portal/labs/${labId}/files/output`, { headers: getHeaders() }),
      ]);
      if (inResp.data.success) {
        setInputFiles(inResp.data.files || []);
      } else if (!quiet) {
        setError(inResp.data.error || 'Failed to load input files');
      }
      if (outResp.data.success) {
        setOutputFiles(outResp.data.files || []);
        setOutSources(outResp.data.sources || []);
        setOutUnmatched(outResp.data.unmatched_count || 0);
      }
    } catch (err: any) {
      if (!quiet) setError(err.response?.data?.error || 'Failed to load files');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLabs(); }, []);

  useEffect(() => {
    if (selectedLabId) fetchFiles(selectedLabId);
    else {
      setInputFiles([]);
      setOutputFiles([]);
    }
  }, [selectedLabId, fetchFiles]);

  // Keep the status column honest while work is happening.
  //
  // The file list was only ever fetched once — right after the upload response —
  // and uploads used to stay 'pending' until a human clicked process, so that
  // single snapshot was accurate. Now that allowlisted labs process on arrival,
  // the report is generated and delivered seconds later and the page had no way to
  // find out: it showed "processing" until the user reloaded, while the PDF was
  // already sitting in the output tab.
  //
  // Re-fetch quietly while anything is in flight and stop the moment everything
  // settles. Scheduling off `inputFiles` means each successful fetch decides
  // whether another is needed, so this self-terminates rather than running on a
  // fixed timer, and the cleanup cancels the pending timeout when the lab changes
  // or the component unmounts.
  const pollCountRef = useRef(0);

  useEffect(() => {
    pollCountRef.current = 0;
  }, [selectedLabId]);

  useEffect(() => {
    if (!selectedLabId) return;

    const inFlight = inputFiles.some(
      (f) => f.status === 'pending' || f.status === 'processing'
    );
    if (!inFlight) {
      pollCountRef.current = 0;
      return;
    }
    if (pollCountRef.current >= MAX_POLLS) return;

    const timer = window.setTimeout(() => {
      pollCountRef.current += 1;
      fetchFiles(selectedLabId, true);
    }, POLL_INTERVAL_MS);

    return () => window.clearTimeout(timer);
  }, [inputFiles, selectedLabId, fetchFiles]);

  const handleAcknowledgeHold = async (rowId: string, accession: string | null) => {
    if (!selectedLabId) return;
    setError(null);
    setSuccess(null);
    try {
      const resp = await axios.post(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLabId}/holds/${rowId}/acknowledge`,
        {},
        { headers: getHeaders() }
      );
      if (resp.data?.success) {
        // Deliberately still listed afterwards: acknowledging records that you
        // have seen it, it does not release the order. Only a re-export with
        // results will clear it, and that happens automatically.
        setSuccess(
          `Acknowledged ${accession || 'order'} — re-export it once results are verified and it will process automatically.`
        );
        fetchFiles(selectedLabId);
      } else {
        setError(resp.data?.error || 'Could not acknowledge');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not acknowledge');
    }
  };

  const handleUpload = async (file: File) => {
    if (!selectedLabId) {
      setError('Please select a lab first');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(`File exceeds 10MB limit (${formatBytes(file.size)})`);
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await axios.post(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLabId}/files/upload`,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      if (resp.data.success) {
        setSuccess(`Uploaded "${resp.data.filename}"`);
        setTab('input');
        // A fresh upload gets a fresh polling budget, even if an earlier cycle
        // exhausted it on a file that never left 'pending'.
        pollCountRef.current = 0;
        await fetchFiles(selectedLabId);
      } else {
        setError(resp.data.error || 'Upload failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (folder: FolderType, filename: string) => {
    if (!selectedLabId) return;
    try {
      const resp = await axios.get(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLabId}/files/download`,
        {
          headers: getHeaders(),
          // `_` is a cache-bust — clinical PDFs can be regenerated in place
          // (same filename, new content); a fresh value per click guarantees
          // the browser doesn't serve a previously-cached blob.
          params: { folder, filename, _: Date.now() },
          responseType: 'blob',
        }
      );
      const blob = new Blob([resp.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Download failed');
    }
  };

  // ── Output-tab filter / sort / select / paginate ─────────────────────
  const filteredSortedOutput = useMemo(() => {
    const search = outSearch.trim().toLowerCase();
    const fromMs = outDateFrom ? new Date(outDateFrom + 'T00:00:00').getTime() : null;
    const toMs = outDateTo ? new Date(outDateTo + 'T23:59:59.999').getTime() : null;

    const filtered = outputFiles.filter((f) => {
      // Source-upload filter runs first: it is the coarsest cut and the one
      // users reach for to isolate a single run's reports.
      if (outSource) {
        const src = f.source_filename || UNKNOWN_SOURCE;
        if (src !== outSource) return false;
      }
      if (search && !f.filename.toLowerCase().includes(search)) return false;
      if (fromMs != null || toMs != null) {
        const t = f.created_at ? new Date(f.created_at).getTime() : NaN;
        if (Number.isNaN(t)) return false;  // no date → exclude from a dated view
        if (fromMs != null && t < fromMs) return false;
        if (toMs != null && t > toMs) return false;
      }
      return true;
    });

    const cmp = (a: LabFile, b: LabFile) => {
      let av: any = (a as any)[outSortKey];
      let bv: any = (b as any)[outSortKey];
      if (outSortKey === 'created_at') {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else if (outSortKey === 'filename') {
        av = String(av || '').toLowerCase();
        bv = String(bv || '').toLowerCase();
      } else {
        av = av ?? 0;
        bv = bv ?? 0;
      }
      if (av < bv) return outSortDir === 'asc' ? -1 : 1;
      if (av > bv) return outSortDir === 'asc' ? 1 : -1;
      return 0;
    };
    return [...filtered].sort(cmp);
  }, [outputFiles, outSource, outSearch, outDateFrom, outDateTo, outSortKey, outSortDir]);

  const pagedOutput = useMemo(
    () => filteredSortedOutput.slice(outPage * outRowsPerPage, (outPage + 1) * outRowsPerPage),
    [filteredSortedOutput, outPage, outRowsPerPage]
  );

  // Reset to page 0 whenever filters/sort change so users don't land on an empty page
  useEffect(() => { setOutPage(0); }, [outSource, outSearch, outDateFrom, outDateTo, outSortKey, outSortDir]);

  // Drop selected filenames that are no longer in the filtered view (filter change semantics)
  useEffect(() => {
    setOutSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(filteredSortedOutput.map((f) => f.filename));
      const next = new Set<string>();
      prev.forEach((n) => { if (visible.has(n)) next.add(n); });
      return next.size === prev.size ? prev : next;
    });
  }, [filteredSortedOutput]);

  const handleSortChange = (key: OutSortKey) => {
    if (outSortKey === key) {
      setOutSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOutSortKey(key);
      setOutSortDir(key === 'filename' ? 'asc' : 'desc');
    }
  };

  const allFilteredSelected =
    filteredSortedOutput.length > 0 && outSelected.size === filteredSortedOutput.length;
  const someFilteredSelected = outSelected.size > 0 && !allFilteredSelected;

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setOutSelected(new Set());
    } else {
      setOutSelected(new Set(filteredSortedOutput.map((f) => f.filename)));
    }
  };

  const toggleOne = (filename: string) => {
    setOutSelected((prev) => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename);
      else next.add(filename);
      return next;
    });
  };

  const handleBulkDownload = async () => {
    if (!selectedLabId || outSelected.size === 0) return;
    setBulkDownloading(true);
    setError(null);
    try {
      const resp = await axios.post(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLabId}/files/download-bulk`,
        {
          folder: 'output',
          filenames: Array.from(outSelected),
          // Name the archive after the run when the selection is scoped to one
          // upload; ".csv" is stripped so it does not read as a CSV archive.
          label: outSource && outSource !== UNKNOWN_SOURCE
            ? `${outSource.replace(/\.csv$/i, '')}_reports`
            : undefined,
        },
        // Cache-bust per click — see comment on the single-file download above.
        { headers: getHeaders(), params: { _: Date.now() }, responseType: 'blob' }
      );
      // Try to honour Content-Disposition filename; fall back to a sensible default
      const cd = (resp.headers['content-disposition'] || '') as string;
      const match = /filename="?([^";]+)"?/.exec(cd);
      const archiveName = match?.[1] || `${selectedLabId}_output_${Date.now()}.zip`;
      const blob = new Blob([resp.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = archiveName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess(`Downloaded ${outSelected.size} file${outSelected.size === 1 ? '' : 's'}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Bulk download failed');
    } finally {
      setBulkDownloading(false);
    }
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">File Transfer</Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={() => selectedLabId && fetchFiles(selectedLabId)}
              disabled={!selectedLabId || loading}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload raw lab reports (CSV, PDF, TXT, HL7) and download ReasonShield processed
        reports. Max file size: 10MB.
      </Typography>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Lab</InputLabel>
          <Select
            value={selectedLabId}
            label="Lab"
            onChange={(e) => setSelectedLabId(e.target.value)}
          >
            {labs.length === 0 && (
              <MenuItem value="" disabled>No labs available</MenuItem>
            )}
            {labs.map((lab) => (
              <MenuItem key={lab.lab_id} value={lab.lab_id}>
                {lab.name} ({lab.lab_id})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            bgcolor: dragActive ? 'action.hover' : 'background.default',
            transition: 'all 0.2s',
            opacity: !selectedLabId || uploading ? 0.6 : 1,
            pointerEvents: !selectedLabId || uploading ? 'none' : 'auto',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            Drag and drop a file here, or
          </Typography>
          <Button
            variant="contained"
            component="label"
            disabled={!selectedLabId || uploading}
            startIcon={<CloudUploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Browse File'}
            <input type="file" hidden onChange={onFileInputChange} />
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            CSV, PDF, TXT, HL7 — up to 10MB
          </Typography>
          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      </Paper>

      <Paper>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={`Uploaded (Input) — ${inputFiles.length}`}
            value="input"
          />
          <Tab
            label={`Processed (Output) — ${outputFiles.length}`}
            value="output"
          />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : tab === 'input' ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Filename</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inputFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No files uploaded yet. Drag a file above to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  inputFiles.map((f) => {
                    const status = f.status || 'pending';
                    return (
                      <React.Fragment key={f.filename}>
                      <TableRow hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InsertDriveFileIcon fontSize="small" color="action" />
                            {f.filename}
                          </Box>
                        </TableCell>
                        <TableCell>{formatBytes(f.size_bytes)}</TableCell>
                        <TableCell>{formatDate(f.uploaded_at)}</TableCell>
                        <TableCell>
                          {(() => {
                            const held = f.held_orders || [];
                            const done = f.order_counts?.processed || 0;
                            const total = f.order_total || 0;
                            // Held orders are always the headline, even when most
                            // of the file processed: those orders have no report
                            // and only the lab can fix them. Showing "Processed"
                            // because 10 of 11 succeeded is how the one that
                            // needs re-exporting goes unnoticed for four days.
                            if (held.length > 0) {
                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Tooltip
                                    title={held
                                      .map((h) => h.reason || h.accession || 'no results transmitted')
                                      .join(' · ')}
                                  >
                                    <Chip
                                      icon={<ErrorOutlineIcon />}
                                      label={`${held.length} order${held.length > 1 ? 's' : ''} need results`}
                                      color="warning"
                                      size="small"
                                    />
                                  </Tooltip>
                                  {done > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {done} of {total} reported
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }
                            if (status === 'processed') {
                              return (
                                <Chip
                                  label={total > 1 ? `Processed (${done}/${total})` : 'Processed'}
                                  color="success"
                                  size="small"
                                />
                              );
                            }
                            if (status === 'processing') {
                              return <Chip label="Processing…" color="info" size="small" />;
                            }
                            if (status === 'failed') {
                              return (
                                <Tooltip title={f.last_error || 'Upload could not be processed'}>
                                  <Chip
                                    icon={<ErrorOutlineIcon />}
                                    label="Upload failed"
                                    color="error"
                                    size="small"
                                  />
                                </Tooltip>
                              );
                            }
                            return <Chip label="Queued for processing" size="small" />;
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {status === 'processed' && f.output_filename && (
                              <Tooltip title="Download report PDF">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleDownload('output', f.output_filename!)}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Download original">
                              <IconButton
                                onClick={() => handleDownload('input', f.filename)}
                                size="small"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {(f.held_orders || []).length > 0 && (
                        <TableRow key={`${f.filename}-held`}>
                          <TableCell colSpan={5} sx={{ py: 1, bgcolor: 'warning.light', opacity: 0.95 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              These orders were not reported — the export carried no results.
                              Re-export each one once it is resulted and verified; it will process
                              automatically and you do not need to resend the rest of the file.
                            </Typography>
                            {(f.held_orders || []).map((h) => (
                              <Box
                                key={h.row_id}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}
                              >
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {h.accession || '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {h.reason}
                                </Typography>
                                {h.acknowledged_at ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Acknowledged${h.acknowledged_by ? ` by ${h.acknowledged_by}` : ''}`}
                                  />
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleAcknowledgeHold(h.row_id, h.accession)}
                                  >
                                    Acknowledge
                                  </Button>
                                )}
                              </Box>
                            ))}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box>
            {/* Output tab toolbar: search / date range / bulk download */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search filename..."
                  value={outSearch}
                  onChange={(e) => setOutSearch(e.target.value)}
                  sx={{ minWidth: 240, flexGrow: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                    ),
                    endAdornment: outSearch ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setOutSearch('')}><ClearIcon fontSize="small" /></IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel id="out-source-label">Source upload</InputLabel>
                  <Select
                    labelId="out-source-label"
                    label="Source upload"
                    value={outSource}
                    onChange={(e) => setOutSource(e.target.value as string)}
                    renderValue={(v) => (v === UNKNOWN_SOURCE ? 'Unknown upload' : (v as string) || 'All uploads')}
                  >
                    <MenuItem value="">
                      All uploads ({outputFiles.length})
                    </MenuItem>
                    {outSources.map((s) => (
                      <MenuItem key={s.filename} value={s.filename}>
                        {/* The stored name is prefixed with the upload timestamp
                            (20260824_075234_…), which is what tells two uploads of
                            the same CSV apart — so show it in full, not truncated. */}
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {s.filename}
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({s.report_count})
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                    {outUnmatched > 0 && (
                      <MenuItem value={UNKNOWN_SOURCE}>
                        Unknown upload
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({outUnmatched})
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={outDateFrom}
                  onChange={(e) => setOutDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={outDateTo}
                  onChange={(e) => setOutDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
                {(outSearch || outDateFrom || outDateTo || outSource) && (
                  <Button
                    size="small"
                    onClick={() => { setOutSearch(''); setOutDateFrom(''); setOutDateTo(''); setOutSource(''); }}
                  >
                    Clear filters
                  </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="contained"
                  startIcon={bulkDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                  onClick={handleBulkDownload}
                  disabled={outSelected.size === 0 || bulkDownloading}
                >
                  {bulkDownloading ? 'Preparing…' : `Download selected (${outSelected.size})`}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {filteredSortedOutput.length} of {outputFiles.length} files
                {outSource ? ` from ${outSource === UNKNOWN_SOURCE ? 'an untraced upload' : outSource}` : ''}
                {outSelected.size > 0 ? ` — ${outSelected.size} selected` : ''}
              </Typography>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someFilteredSelected}
                        checked={allFilteredSelected}
                        onChange={toggleSelectAllFiltered}
                        disabled={filteredSortedOutput.length === 0}
                        inputProps={{ 'aria-label': 'Select all filtered' }}
                      />
                    </TableCell>
                    <TableCell sortDirection={outSortKey === 'filename' ? outSortDir : false}>
                      <TableSortLabel
                        active={outSortKey === 'filename'}
                        direction={outSortKey === 'filename' ? outSortDir : 'asc'}
                        onClick={() => handleSortChange('filename')}
                      >Filename</TableSortLabel>
                    </TableCell>
                    <TableCell>Source upload</TableCell>
                    <TableCell sortDirection={outSortKey === 'size_bytes' ? outSortDir : false}>
                      <TableSortLabel
                        active={outSortKey === 'size_bytes'}
                        direction={outSortKey === 'size_bytes' ? outSortDir : 'asc'}
                        onClick={() => handleSortChange('size_bytes')}
                      >Size</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={outSortKey === 'created_at' ? outSortDir : false}>
                      <TableSortLabel
                        active={outSortKey === 'created_at'}
                        direction={outSortKey === 'created_at' ? outSortDir : 'asc'}
                        onClick={() => handleSortChange('created_at')}
                      >Created</TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedOutput.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {outputFiles.length === 0
                          ? 'No processed reports available yet.'
                          : 'No files match the current filters.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedOutput.map((f) => {
                      const checked = outSelected.has(f.filename);
                      return (
                        <TableRow key={f.filename} hover selected={checked}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleOne(f.filename)}
                              inputProps={{ 'aria-label': `Select ${f.filename}` }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InsertDriveFileIcon fontSize="small" color="action" />
                              {f.filename}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            {f.source_filename ? (
                              <Tooltip title={`Show only reports from ${f.source_filename}`}>
                                <Chip
                                  size="small"
                                  variant={outSource === f.source_filename ? 'filled' : 'outlined'}
                                  color={outSource === f.source_filename ? 'primary' : 'default'}
                                  label={f.source_filename}
                                  // One click to isolate the run this report came
                                  // from — the common case is "give me the rest of
                                  // these", and hunting the same name in the picker
                                  // is the slow way to say it.
                                  onClick={() => setOutSource(
                                    outSource === f.source_filename ? '' : f.source_filename!
                                  )}
                                  sx={{ maxWidth: 260, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>{formatBytes(f.size_bytes)}</TableCell>
                          <TableCell>{formatDate(f.created_at)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Download">
                              <IconButton size="small" onClick={() => handleDownload('output', f.filename)}>
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredSortedOutput.length}
              page={outPage}
              onPageChange={(_, p) => setOutPage(p)}
              rowsPerPage={outRowsPerPage}
              onRowsPerPageChange={(e) => { setOutRowsPerPage(parseInt(e.target.value, 10)); setOutPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default FileUploadManager;
